import { Button, Dialog, Flex, Text, TextField } from '@radix-ui/themes';
import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../config/axiosConfig';
import ErrorPopup from './ErrorPopup';
import DialogCloseButton from './DialogCloseButton';

// Yup validation schema
const validationSchema = Yup.object({
  prefix: Yup.string()
    .max(2, 'Prefix must be at most 2 characters')
    .optional(),
  roomNumberFrom: Yup.number()
    .typeError('Room Number From must be a number')
    .positive('Room Number From must be a positive number')
    .integer('Room Number From must be an integer')
    .required('Room Number From is required'),
  roomNumberTo: Yup.number()
    .typeError('Room Number To must be a number')
    .positive('Room Number To must be a positive number')
    .integer('Room Number To must be an integer')
    .required('Room Number To is required')
    .min(Yup.ref('roomNumberFrom'), 'Room Number To must be greater than or equal to Room Number From'),
  suffix: Yup.string()
    .max(2, 'Suffix must be at most 2 characters')
    .optional(),
});

const CreateRoom = ({ onRoomCreated, showToast }) => {
  const initialFormData = {
    prefix: '',
    roomNumberFrom: '',
    roomNumberTo: '',
    suffix: '',
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorOpen, setIsErrorOpen] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true);
    try {
      const roomData = {
        prefix: values.prefix,
        roomNumberFrom: parseInt(values.roomNumberFrom) || 0,
        roomNumberTo: parseInt(values.roomNumberTo) || 0,
        suffix: values.suffix,
      };

      const response = await axiosInstance.post('/Room/add-rooms-range', roomData);

      if (response.data.result) {
        console.log('Rooms created successfully');
        if (onRoomCreated) await onRoomCreated();
        if (showToast) showToast('Rooms created successfully');
        resetForm(); // Reset form on successful submission
        setIsOpen(false);
      } else {
        setErrorMessage(response.data.errorResponse?.message || 'Failed to create rooms');
        setIsErrorOpen(true);
      }
    } catch (error) {
      const message = error.response?.data?.errorResponse?.message || 'Error creating rooms';
      setErrorMessage(message);
      setIsErrorOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogChange = (open) => {
    setIsOpen(open);
    if (!open) {
      // Formik's resetForm will handle resetting the form when dialog closes
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleDialogChange}>
        <Dialog.Trigger>
          <Button onClick={() => setIsOpen(true)}>Add Rooms</Button>
        </Dialog.Trigger>

        <Dialog.Content maxWidth="650px">
          <DialogCloseButton onClose={() => setIsOpen(false)} />
          <Dialog.Title>Add Rooms</Dialog.Title>

          <Formik
            initialValues={initialFormData}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting: formikIsSubmitting }) => (
              <Form>
                <Flex direction="column" gap="3">
                  <Flex gap="3">
                    <label className="w-[25%]">
                      <Text as="div" size="2" mb="1" weight="bold">Prefix</Text>
                      <Field
                        as={TextField.Root}
                        name="prefix"
                        placeholder="Enter Prefix"
                        maxLength={2}
                      />
                      <ErrorMessage
                        name="prefix"
                        render={msg => (
                          <Text size="1" color="red" mt="1">{msg}</Text>
                        )}
                      />
                    </label>
                    <label className="w-[25%]">
                      <Text as="div" size="2" mb="1" weight="bold">Room Number From</Text>
                      <Field
                        as={TextField.Root}
                        name="roomNumberFrom"
                        placeholder="Enter Room From"
                        type="number"
                      />
                      <ErrorMessage
                        name="roomNumberFrom"
                        render={msg => (
                          <Text size="1" color="red" mt="1">{msg}</Text>
                        )}
                      />
                    </label>
                    <label className="w-[25%]">
                      <Text as="div" size="2" mb="1" weight="bold">Room Number To</Text>
                      <Field
                        as={TextField.Root}
                        name="roomNumberTo"
                        placeholder="Enter Room To"
                        type="number"
                      />
                      <ErrorMessage
                        name="roomNumberTo"
                        render={msg => (
                          <Text size="1" color="red" mt="1">{msg}</Text>
                        )}
                      />
                    </label>
                    <label className="w-[25%]">
                      <Text as="div" size="2" mb="1" weight="bold">Suffix</Text>
                      <Field
                        as={TextField.Root}
                        name="suffix"
                        placeholder="Enter Suffix"
                        maxLength={2}
                      />
                      <ErrorMessage
                        name="suffix"
                        render={msg => (
                          <Text size="1" color="red" mt="1">{msg}</Text>
                        )}
                      />
                    </label>
                  </Flex>
                </Flex>

                <Flex gap="3" mt="4" justify="end">
                  <Button
                    variant="soft"
                    color="gray"
                    onClick={() => handleDialogChange(false)}
                    disabled={isSubmitting || formikIsSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || formikIsSubmitting}
                  >
                    Add
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Root>
      <ErrorPopup
        errorMessage={errorMessage}
        isOpen={isErrorOpen}
        onClose={() => setIsErrorOpen(false)}
      />
    </>
  );
};

export default CreateRoom;